import type { ReviewReportMetadata } from "./reviewReport";

export const REVIEW_OUTCOME_LABELS = {
  approved: "Approved",
  comment: "Comments",
  changes_requested: "Changes requested",
} as const;

export const REVIEW_OUTCOME_CLASSES = {
  approved: "text-ds-green",
  comment: "text-neutral-700 dark:text-neutral-300",
  changes_requested: "text-amber-600 dark:text-amber-400",
} as const;

export const REVIEW_DISPOSITION_LABELS = {
  fix: "Fix",
  wont_fix: "Won't fix",
  fast_follow: "Fast follow",
} as const;

const APPROVAL_SKIP_REASONS = new Map([
  ["risk_missing", "risk was not assessed"],
  ["findings_not_posted", "the findings were never posted"],
  ["reviewed_base_missing", "the reviewed base is unknown"],
  ["draft", "the PR is a draft"],
  ["closed", "the PR is closed"],
  ["github_unavailable", "GitHub was unavailable"],
]);

const PUSHED_PAST_SKIP_REASONS = new Set([
  "head_moved",
  "base_changed",
  "merge_base_changed",
]);

const NOT_APPLICABLE_SKIP_REASONS = new Set([
  "auto_approve_off",
  "bot_author",
  "fork",
]);

export type ReviewApprovalTone =
  | "pending"
  | "attention"
  | "success"
  | "success-muted"
  | "danger"
  | "neutral";

export interface ReviewApprovalPill {
  label: string;
  tone: ReviewApprovalTone;
  /** One line saying why. */
  tip: string;
}

/** Status-pill dots per DESIGN.md › Status chips, including its review-card exception for green. */
export const REVIEW_APPROVAL_DOT_CLASSES = {
  pending: "bg-[var(--chart-9)]",
  attention: "bg-[var(--chart-3)]",
  success: "bg-[var(--chart-12)]",
  "success-muted": "bg-[var(--chart-12)] opacity-50",
  danger: "bg-[var(--chart-5)]",
  neutral: "bg-[var(--chart-grey)]",
} as const satisfies Record<ReviewApprovalTone, string>;

/**
 * Auto-approve applies to the report's PR author; reports mirrored before
 * `approval` existed apply once they carry a verdict. A recorded approval
 * decision keeps its pill after auto-approve is turned off.
 */
export function reviewAutoApproveApplies(
  report: Pick<ReviewReportMetadata, "approval" | "verdict">,
): boolean {
  const decision = report.verdict?.approval_decision;
  if (decision != null && decision !== "skipped") return true;
  return report.approval?.applies ?? report.verdict !== undefined;
}

function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

const SUPERSEDED_PILL: ReviewApprovalPill = {
  label: "Superseded",
  tone: "neutral",
  tip: "A newer review replaced this round",
};

/**
 * The one approval-state pill for a review round, e.g. `Fixing 2 findings`;
 * null when auto-approve does not apply or there is nothing to say. A round
 * that is not its PR's latest never reads as still pending.
 */
export function reviewApprovalPill(
  report: Pick<
    ReviewReportMetadata,
    "approval" | "verdict" | "review_result" | "wake"
  >,
  { isLatest }: { isLatest: boolean },
): ReviewApprovalPill | null {
  if (!reviewAutoApproveApplies(report)) return null;
  const { verdict } = report;
  if (!verdict) {
    return isLatest
      ? {
          label: "Awaiting approval decision",
          tone: "pending",
          tip: "The lead agent hasn't recorded a verdict yet",
        }
      : null;
  }
  const toFix = verdict.findings.filter(
    (finding) => finding.disposition === "fix",
  );
  const declined = verdict.findings.length - toFix.length;
  const pending = (pill: ReviewApprovalPill) =>
    isLatest ? pill : SUPERSEDED_PILL;

  if (verdict.verdict === "not_yet") {
    if (isLatest && toFix.length > 0) {
      const labels = toFix.map((finding) => finding.label).join(", ");
      const asked = `${labels}${declined > 0 ? `; ${declined} declined` : ""}`;
      if (report.wake) {
        return {
          label: `${count(toFix.length, "finding")} to fix`,
          tone: "attention",
          tip: `${
            report.wake.skipped_reason === "round_cap"
              ? "Fixes wait for more review rounds"
              : "Fixes wait for autofix"
          }: ${asked}`,
        };
      }
      return {
        label: `Fixing ${count(toFix.length, "finding")}`,
        tone: "attention",
        tip: `Lead asked for fixes: ${asked}`,
      };
    }
    const parts = [
      toFix.length > 0 ? `${toFix.length} to fix` : null,
      declined > 0 ? `${declined} declined` : null,
    ].filter((part) => part !== null);
    return {
      label: "Not auto-approved",
      tone: "neutral",
      tip: `Lead didn't approve${
        parts.length > 0 ? `: ${parts.join(", ")}` : ""
      }`,
    };
  }

  const approvedTip =
    declined > 0
      ? `Lead approved; ${count(declined, "finding")} declined`
      : "Lead approved with no open findings";
  const { approval_decision: decision, approval_skip_reason: skipReason } =
    verdict;
  switch (decision) {
    case null:
      return pending({
        label: "Approval pending",
        tone: "pending",
        tip: "The approval is queued",
      });
    case "approving":
      return pending({
        label: "Approval pending",
        tone: "pending",
        tip: "The approval is being submitted to GitHub",
      });
    case "unconfirmed":
      return {
        label: "Approval unconfirmed",
        tone: "neutral",
        tip: "GitHub didn't confirm the approval; check the PR",
      };
    case "approved":
      return { label: "Approved", tone: "success", tip: approvedTip };
    case "would_approve":
      return {
        label: "Would approve (shadow)",
        tone: "success-muted",
        tip: approvedTip,
      };
    case "withdrawing":
    case "withdrawn":
      return {
        label: "Approval withdrawn",
        tone: "neutral",
        tip: "The approval was withdrawn",
      };
    case "would_withdraw":
      return {
        label: "Would withdraw (shadow)",
        tone: "neutral",
        tip: "The approval would have been withdrawn",
      };
    case "refused":
      return {
        label: "Not auto-approved",
        tone: "neutral",
        tip: "GitHub refused the approval",
      };
    case "skipped":
      break;
    default:
      return null;
  }

  if (skipReason === "already_approved") {
    return {
      label: "Covered by earlier approval",
      tone: "neutral",
      tip: "An earlier round already covers this head",
    };
  }
  if (skipReason === "proof_running") {
    return pending({
      label: "Waiting for Proof",
      tone: "pending",
      tip: "Approval waits for Proof to finish",
    });
  }
  if (skipReason === "proof_issues") {
    return {
      label: "Proof found issues",
      tone: "danger",
      tip: "Proof found issues on this commit",
    };
  }
  if (skipReason === "risk_not_low") {
    const risk = report.approval?.risk ?? report.review_result.risk?.level;
    return {
      label: "Needs human approval",
      tone: "attention",
      tip: risk
        ? `Auto-approve needs low risk; this round is ${risk}`
        : "Auto-approve needs low risk",
    };
  }
  if (skipReason && PUSHED_PAST_SKIP_REASONS.has(skipReason)) {
    return {
      label: "Superseded by a push",
      tone: "neutral",
      tip: "New commits were pushed before approval",
    };
  }
  if (skipReason && NOT_APPLICABLE_SKIP_REASONS.has(skipReason)) return null;
  const phrase = skipReason
    ? APPROVAL_SKIP_REASONS.get(skipReason) ?? skipReason.replaceAll("_", " ")
    : null;
  return {
    label: "Not auto-approved",
    tone: "neutral",
    tip: phrase ? `Not approved: ${phrase}` : "Not approved",
  };
}

// Place it between whitespace-nowrap segments, never inside one: the NBSP
// keeps the middot with the segment before it, and the plain space after it
// is the line's only wrap point.
export const REVIEW_DETAIL_SEPARATOR = "\u00a0· ";

/**
 * Status-pill dots per DESIGN.md: the chip stays neutral, the dot carries the
 * outcome. These are review outcomes, not run statuses: `STATUS_COLORS.success`
 * is blue and would read the same as the indigo comment dot.
 */
export const REVIEW_OUTCOME_DOT_CLASSES = {
  approved: "bg-[var(--chart-12)]",
  comment: "bg-[var(--chart-9)]",
  changes_requested: "bg-[var(--chart-3)]",
} as const;
